import React from "react";
import { formatValue } from "./helpers";
import {
  OcCard,
  OcInfoCardAddress,
  OcInfoCardDivider,
  OcInfoCardItem,
  OcInfoCardLabel,
  OcInfoCardPeriod,
} from "oc/components";

interface InfoCardViewProps {
  def: any; // Replace 'any' with a more specific type if available
  data: any;
  editable: boolean;
  onEdit: (...args: any[]) => void;
  hasChangeRequest?: (args: any) => boolean;
  label: string;
  t: (key: string) => string;
  i18n?: any;
  defaultLabel?: string;
  lastDivider?: number;
}

export const InfoCardView: React.FC<InfoCardViewProps> = ({
  def,
  data,
  editable,
  onEdit,
  hasChangeRequest = () => false,
  label,
  t,
  i18n,
  defaultLabel,
  lastDivider,
}) => {
  const renderEmailItems = () => (
    <OcCard label={label}>
      {data?.emails?.map(
        (item: { type: string; address: string }, idx: number) => (
          <OcInfoCardItem
            key={item.type + idx}
            label={t(`MY_HR_EMAIL_${item.type.toUpperCase()}`)}
            value={item.address}
            type={item.type}
            editable={editable}
            onEdit={() => onEdit("emails", item.type)}
            isEditButtonDisabled={hasChangeRequest({
              type: `${item.type}Email`,
            })}
          />
        ),
      )}
    </OcCard>
  );

  const renderPhoneItems = () => (
    <OcCard label={label}>
      {data?.phones?.map(
        (item: { type: string; number: string }, idx: number) => (
          <OcInfoCardItem
            key={item.type + idx}
            label={t(`MY_HR_PHONE_${item.type.toUpperCase()}`)}
            value={item.number}
            type={item.type}
            editable={editable}
            onEdit={() => onEdit("phones", item.type)}
            isEditButtonDisabled={hasChangeRequest({
              type: `${item.type}Phone`,
            })}
          />
        ),
      )}
    </OcCard>
  );

  const renderName = () => (
    <OcCard
      label={
        <OcInfoCardLabel
          label={label}
          editable={editable}
          onEdit={onEdit}
          type={def.type}
          editButtonDisabled={hasChangeRequest({ typeGroup: "name" })}
        />
      }
    >
      {def.children.map(
        (
          item: {
            translation?: string;
            label?: string;
            value?: string;
            type?: string;
          },
          i: number,
        ) => {
          if (!data || !item.value) return null;
          return (
            <OcInfoCardItem
              key={i}
              label={t(item.translation ?? "") || item.label || ""}
              value={item.value ? data[item.value] : ""}
              translation={item.translation}
              type={item.type}
              editable={false}
            />
          );
        },
      )}
    </OcCard>
  );

  const renderGeneric = () => (
    <OcCard label={label}>
      {def.children.map(
        (
          item: {
            translation?: string;
            label?: string;
            value?: string;
            type?: string;
          },
          i: number,
        ) => {
          if (item.type === "address") return null; // handled above

          if (item.type === "divider") {
            if (i === lastDivider || data.employmentType?.code === "5")
              return null;
            return <OcInfoCardDivider key={i} />;
          }
          if (item.type === "period") {
            return (
              <OcInfoCardPeriod
                key={i}
                validFrom={data.validFrom}
                validTo={data.validTo}
              />
            );
          }
          const isEditable =
            editable &&
            !["dateOfBirth", "taxNumber", "socialSecurityNumber"].includes(
              item.type ?? "",
            );
          return (
            <OcInfoCardItem
              key={i}
              label={t(item.translation ?? "") || item.label || ""}
              value={formatValue(
                {
                  type: item.type ?? "",
                  value: item.value ?? "",
                },
                data,
                i18n,
              )}
              translation={item.translation}
              type={item.type}
              editable={isEditable}
              onEdit={onEdit}
              isEditButtonDisabled={hasChangeRequest({ type: item.type })}
            />
          );
        },
      )}
    </OcCard>
  );

  switch (def.type) {
    case "address":
      return (
        <OcInfoCardAddress
          label={def.label}
          translation={def.translation}
          address={data?.[def.address]}
          def={def}
          onEdit={() => onEdit(def.address, null)}
          isEditButtonDisabled={hasChangeRequest({ typeGroup: def.address })}
          editable={editable}
          defaultLabel={defaultLabel}
        />
      );
    case "email":
      return renderEmailItems();
    case "phone":
      return renderPhoneItems();
    case "name":
      return renderName();
    default:
      return renderGeneric();
  }
};
